/**
 * The onboarding checklist progress card (plan docs/plans/lms-plan.md 7.1,
 * 12.3, 18.24; R1, R14; WP-41): shown on Home only while an onboarding run
 * is active (GET /me/onboarding answers the run, or null when there is none),
 * with how many items are complete (done or not applicable, 12.4), how many
 * are overdue or wait for an admin's verification, and a link to the
 * Onboarding tab where the items are worked (WP-47).
 */
import { Link } from 'react-router-dom';
import { ArrowRight, ListChecks } from 'lucide-react';
import { secondaryBtnCls } from '../../../ui/styles';
import { formatDay } from '../../components/DueBadge';
import { Pill } from '../../components/StatusPill';
import { sectionCardCls } from './Section';
import type { MyOnboarding } from '../../api/me';

export function OnboardingCard({ onboarding }: { onboarding: MyOnboarding }) {
  const p = onboarding.progress;
  const total = Math.max(0, p?.total ?? 0);
  const complete = Math.max(0, (p?.done ?? 0) + (p?.not_applicable ?? 0));
  const pct = total > 0 ? Math.min(100, Math.round((complete / total) * 100)) : 0;
  const started = formatDay(onboarding.run?.start_date);
  return (
    <section className={`${sectionCardCls} p-4`} aria-labelledby="lms-home-onboarding-title" data-testid="lms-onboarding-card">
      <h2 id="lms-home-onboarding-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <ListChecks className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Onboarding checklist
      </h2>
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-testid="lms-onboarding-count">
        {`${complete} of ${total} items complete`}
      </p>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="progressbar"
        aria-label="Onboarding checklist progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="h-full rounded-full bg-primary-600 dark:bg-primary-400" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {p?.overdue ? <Pill tone="red">{`${p.overdue} overdue`}</Pill> : null}
        {p?.awaiting_verification ? <Pill tone="amber">{`${p.awaiting_verification} awaiting verification`}</Pill> : null}
        {started ? <span className="text-xs text-gray-500 dark:text-gray-400">{`Started ${started}`}</span> : null}
      </div>
      <Link to="/lms/onboarding" className={`${secondaryBtnCls} mt-3`} data-testid="lms-onboarding-link">
        Open your checklist
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </section>
  );
}

export default OnboardingCard;
